import { type Paging } from 'api/types';

export const ActiveStatus = {
  Published: 'PUBLISHED',
  Reserved: 'RESERVED',
} as const;

export const HistoryDonationStatus = {
  Collected: 'COLLECTED',
  NoShow: 'NO_SHOW',
  CancelledByStore: 'CANCELLED_BY_STORE',
  CancelledByCharity: 'CANCELLED_BY_CHARITY',
  Declined: 'DECLINED',
} as const;

export const OrderState = {
  Redeemed: 'REDEEMED',
  Unredeemed: 'UNREDEEMED',
  Cancelled: 'CANCELLED',
} as const;

export type OrderStateType = (typeof OrderState)[keyof typeof OrderState];

export const DetailTerminalStatus = {
  CollectedWithCode: 'COLLECTED_WITH_CODE',
  CollectedWithoutCode: 'COLLECTED_WITHOUT_CODE',
  CancelledByStore: 'CANCELLED_BY_STORE',
  CancelledByCharity: 'CANCELLED_BY_CHARITY',
  notReserved: 'NOT_RESERVED',
} as const;

export const DonationStatus = {
  ...ActiveStatus,
  ...HistoryDonationStatus,
  ...DetailTerminalStatus,
} as const;

export type ActiveStatusType = (typeof ActiveStatus)[keyof typeof ActiveStatus];
export type HistoryDonationStatusType =
  (typeof HistoryDonationStatus)[keyof typeof HistoryDonationStatus];
export type DetailStatusType =
  | ActiveStatusType
  | (typeof DetailTerminalStatus)[keyof typeof DetailTerminalStatus];
export type DonationStatusType = (typeof DonationStatus)[keyof typeof DonationStatus];

export type ActiveDonationItem = {
  itemId: string;
  orderId: string | null;
  storeId: string;
  storeName: string;
  storeLogoUrl: string | null;
  numberOfProducts: number;
  collectionWindowStart: string;
  collectionWindowEnd: string;
  status: ActiveStatusType;
};

export type ActiveDonationsResponse = {
  donations: ActiveDonationItem[];
  paging: Paging;
};

export type DonationProductWeight = {
  value: number;
  unit: string;
};

export type DonationProduct = {
  productId: string;
  ean?: string;
  name: string;
  logoUrl?: string;
  quantity: number;
  weight?: DonationProductWeight;
  isFoodItem: boolean;
};

export type DonationCategory = {
  code: string;
  label: string;
  isFood: boolean;
};

export type DonationBase = {
  donationId: string;
  storeId: string;
  storeName: string;
  storeLogoUrl?: string;
  collectionWindowStart: string;
  collectionWindowEnd: string;
  orderId?: string;
  numberOfProducts: number;
  products: DonationProduct[];
  charityItemState: DetailStatusType;
  categories: DonationCategory[];
};

export type DonationDetailAdditionalFields = {
  storeAddress?: string;
  storePhoneNumber?: string;
  collectionInfo?: string;
  orderState?: OrderStateType;
  allergensUrl?: string;
  allergensTitle?: string;
  allergensDescription?: string;
  requiresCollectionCode: boolean;
  collectionCode?: string;
};

export type DonationDetail = DonationBase & DonationDetailAdditionalFields;

export type DonationDetailView = Omit<
  DonationDetail,
  'requiresCollectionCode' | 'collectionCode' | 'charityItemState'
>;

export type DonationDetailFromAPI = Omit<DonationDetail, 'donationId'> & { itemId: string };

export type HistoryDonation = {
  itemId: string;
  storeId: string;
  storeName: string;
  storeLogoUrl: string | null;
  numberOfProducts: number;
  publishedDate?: string;
  collectedDate?: string;
  storeAddress?: string;
  status: HistoryDonationStatusType;
};

export type DonationHistoryResponse = {
  donations: HistoryDonation[];
  paging: Paging;
};

export const HistorySortBy = {
  StoreName: 'STORE_NAME',
  NumberOfProducts: 'NUMBER_OF_PRODUCTS',
  PublishedDate: 'PUBLISHED_DATE',
  CollectedDate: 'COLLECTED_DATE',
  StoreAddress: 'STORE_ADDRESS',
  Status: 'STATUS',
} as const;

export type HistorySortByType = (typeof HistorySortBy)[keyof typeof HistorySortBy];
export type HistorySortDirection = 'ASC' | 'DESC';

export type SortDirection = 'asc' | 'desc';

export type HistorySort = { sortBy: HistorySortByType; direction: SortDirection };

export type GetDonationHistoryParams = {
  page: number;
  size: number;
  sort: HistorySort;
  storeFilter?: string[] | null;
  statusFilter?: string[] | null;
};

export type HistoryFilterStore = {
  storeId: string;
  storeName: string;
  storeLogoUrl: string;
};
